import React from "react";

import {
  Alert,
  Box,
  Button,
  Chip,
  ChipProps,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

import DatasetOutlinedIcon from "@mui/icons-material/DatasetOutlined";

import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";

import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";

import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";

import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";

import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";

import {
  BieRecordId,
  getBieImportClassLabel,
} from "./bieonboardingworkbench.models";

import BieOnboardingStatusChip from "./BieOnboardingStatusChip";

/* ============================================================
 * Workbench staging models
 *
 * These can later be moved into:
 *
 * bieonboardingworkbench.models.ts
 * ============================================================
 */

export interface IBieOnboardingStagedImportSummary {
  rid?: BieRecordId | null;

  classCode: string;
  className?: string;

  sheetName: string;

  totalRows: number;
  stagedRows: number;
  warningRows: number;
  rejectedRows: number;

  statusCode: "STAGED" | "WARNING" | "FAILED" | "EMPTY" | string;

  message?: string;

  extraInfo?: Record<string, unknown>;
}

export interface IBieOnboardingWorkbenchStagingData {
  loadSessionId: BieRecordId;

  stamp?: string;
  stateCode?: string;

  filename?: string;

  workbookVersion?: string;
  templateVersion?: string;

  imports: IBieOnboardingStagedImportSummary[];

  warnings?: string[];
  errors?: string[];
}

/* ============================================================
 * Props
 * ============================================================
 */

export interface IBieOnboardingWorkbenchStagingProps {
  review: IBieOnboardingWorkbenchStagingData | null;

  loading?: boolean;
  refreshing?: boolean;
  disabled?: boolean;

  title?: string;
  description?: string;

  onRefresh?: (loadSessionId: BieRecordId) => void;

  /**
   * Opens the staged records belonging to one Import.
   */
  onOpenImportRecords?: (
    importId: BieRecordId,
    item: IBieOnboardingStagedImportSummary,
  ) => void;
}

/* ============================================================
 * Internal types
 * ============================================================
 */

interface IBieStagingTotals {
  imports: number;

  totalRows: number;
  stagedRows: number;
  warningRows: number;
  rejectedRows: number;

  stagedImports: number;
  warningImports: number;
  failedImports: number;
  emptyImports: number;
}

type BieStagingTone = "default" | "success" | "warning" | "error" | "info";

/* ============================================================
 * Component
 * ============================================================
 */

/**
 * Workbench tab for reviewing staging and reference-resolution
 * outcomes for one Load Session.
 *
 * The component does not execute staging and does not obtain or
 * infer loadSessionId. The parent Workbench owns the Stage and
 * Resolve action and supplies the refreshed review result.
 *
 * review.loadSessionId remains the authoritative parent ID for
 * refresh and staged-record drill-down actions.
 */
export default class BieOnboardingWorkbenchStaging extends React.PureComponent<IBieOnboardingWorkbenchStagingProps> {
  public render(): React.ReactNode {
    const { loading = false, review } = this.props;

    if (loading && !review) {
      return this.renderLoading();
    }

    if (!review) {
      return this.renderUnavailable();
    }

    return (
      <Stack spacing={2}>
        {this.props.refreshing && <LinearProgress />}

        {this.renderHeader()}

        {this.renderMessages()}

        {this.renderSummary()}

        {this.renderImports()}
      </Stack>
    );
  }

  /* ==========================================================
   * Header
   * ==========================================================
   */

  private renderHeader(): React.ReactNode {
    const review = this.requireReview();

    const totals = this.getTotals(review);

    const overallTone = this.getOverallTone(review, totals);

    return (
      <Paper
        variant="outlined"
        sx={{
          position: "relative",
          overflow: "hidden",

          p: {
            xs: 2,
            md: 2.5,
          },

          "&::before": {
            content: '""',

            position: "absolute",
            top: 0,
            left: 0,

            width: 5,
            height: "100%",

            backgroundColor: (theme) => {
              switch (overallTone) {
                case "success":
                  return theme.palette.success.main;

                case "warning":
                  return theme.palette.warning.main;

                case "error":
                  return theme.palette.error.main;

                case "info":
                  return theme.palette.info.main;

                default:
                  return theme.palette.divider;
              }
            },
          },
        }}
      >
        <Stack
          direction={{
            xs: "column",
            md: "row",
          }}
          justifyContent="space-between"
          alignItems={{
            xs: "stretch",
            md: "flex-start",
          }}
          spacing={2}
        >
          <Stack direction="row" spacing={1.25} alignItems="flex-start">
            <Box
              sx={{
                width: 42,
                height: 42,

                flexShrink: 0,

                display: "flex",
                alignItems: "center",
                justifyContent: "center",

                borderRadius: 2,

                color: this.getToneTextColor(overallTone),

                backgroundColor: this.getToneBackground(overallTone),
              }}
            >
              {this.getOverallIcon(overallTone)}
            </Box>

            <Box
              sx={{
                minWidth: 0,
              }}
            >
              <Typography variant="h6" fontWeight={700}>
                {this.props.title || "Staging & Resolution"}
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mt: 0.4,
                }}
              >
                {this.props.description ||
                  "Review worksheet staging, reference-resolution " +
                    "outcomes, and unresolved records before validation."}
              </Typography>

              <Stack
                direction="row"
                spacing={0.75}
                useFlexGap
                flexWrap="wrap"
                sx={{
                  mt: 1,
                }}
              >
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Load Session ${String(review.loadSessionId)}`}
                />

                {review.filename && (
                  <Tooltip title={review.filename} arrow>
                    <Chip
                      size="small"
                      icon={<DescriptionOutlinedIcon />}
                      label={review.filename}
                      sx={{
                        maxWidth: 320,

                        "& .MuiChip-label": {
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        },
                      }}
                    />
                  </Tooltip>
                )}

                {review.workbookVersion && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Workbook ${review.workbookVersion}`}
                  />
                )}

                {review.templateVersion && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Template ${review.templateVersion}`}
                  />
                )}
              </Stack>
            </Box>
          </Stack>

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            useFlexGap
            flexWrap="wrap"
            sx={{
              flexShrink: 0,
            }}
          >
            <BieOnboardingStatusChip
              kind="LOAD_SESSION"
              value={review.stateCode || "STAGED"}
            />

            <Chip
              size="small"
              variant="outlined"
              color={this.getChipColor(overallTone)}
              icon={this.getOverallIcon(overallTone)}
              label={this.getOverallLabel(overallTone)}
            />

            {this.props.onRefresh && (
              <Tooltip title="Refresh staging review" arrow>
                <span>
                  <IconButton
                    disabled={this.isDisabled()}
                    onClick={this.handleRefresh}
                  >
                    {this.props.refreshing ? (
                      <CircularProgress size={22} />
                    ) : (
                      <RefreshOutlinedIcon />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Stack>
        </Stack>
      </Paper>
    );
  }

  /* ==========================================================
   * Messages
   * ==========================================================
   */

  private renderMessages(): React.ReactNode {
    const review = this.requireReview();

    const errors = review.errors || [];

    const warnings = review.warnings || [];

    if (errors.length === 0 && warnings.length === 0) {
      return (
        <Alert severity="success" icon={<CheckCircleOutlineIcon />}>
          Staging completed without general errors or warnings.
        </Alert>
      );
    }

    return (
      <Stack spacing={1.5}>
        {errors.length > 0 && (
          <Alert severity="error">
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{
                mb: 0.5,
              }}
            >
              Workbook staging errors
            </Typography>

            <Stack spacing={0.35}>
              {errors.map((message, index) => (
                <Typography key={index} variant="body2">
                  • {message}
                </Typography>
              ))}
            </Stack>
          </Alert>
        )}

        {warnings.length > 0 && (
          <Alert severity="warning">
            <Typography
              variant="subtitle2"
              fontWeight={700}
              sx={{
                mb: 0.5,
              }}
            >
              Workbook staging warnings
            </Typography>

            <Stack spacing={0.35}>
              {warnings.map((message, index) => (
                <Typography key={index} variant="body2">
                  • {message}
                </Typography>
              ))}
            </Stack>
          </Alert>
        )}
      </Stack>
    );
  }

  /* ==========================================================
   * Summary
   * ==========================================================
   */

  private renderSummary(): React.ReactNode {
    const review = this.requireReview();

    const totals = this.getTotals(review);

    return (
      <Paper
        variant="outlined"
        sx={{
          p: 2,
        }}
      >
        <Typography variant="h6" fontWeight={700}>
          Staging Summary
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mt: 0.35,
          }}
        >
          Aggregate results from all recognized workbook worksheets.
        </Typography>

        <Divider
          sx={{
            my: 2,
          }}
        />

        <Box
          sx={{
            display: "grid",

            gridTemplateColumns: {
              xs: "repeat(2, minmax(0, 1fr))",

              sm: "repeat(3, minmax(0, 1fr))",

              lg: "repeat(6, minmax(0, 1fr))",
            },

            gap: 1.5,
          }}
        >
          {this.renderMetric("Imports", totals.imports, "default")}

          {this.renderMetric("Source Rows", totals.totalRows, "info")}

          {this.renderMetric("Staged Rows", totals.stagedRows, "success")}

          {this.renderMetric("Warning Rows", totals.warningRows, "warning")}

          {this.renderMetric("Rejected Rows", totals.rejectedRows, "error")}

          {this.renderMetric(
            "Failed Imports",
            totals.failedImports,
            totals.failedImports > 0 ? "error" : "success",
          )}
        </Box>
      </Paper>
    );
  }

  private renderMetric(
    label: string,
    value: number,
    tone: BieStagingTone,
  ): React.ReactNode {
    return (
      <Box
        sx={{
          p: 1.5,

          minHeight: 94,

          borderRadius: 1.5,

          border: "1px solid",

          borderColor: this.getToneBorderColor(tone),

          backgroundColor: this.getToneBackground(tone),
        }}
      >
        <Typography variant="caption" color="text.secondary" fontWeight={700}>
          {label}
        </Typography>

        <Typography
          variant="h5"
          fontWeight={700}
          color={this.getToneTextColor(tone)}
          sx={{
            mt: 0.5,
          }}
        >
          {this.formatNumber(value)}
        </Typography>
      </Box>
    );
  }

  /* ==========================================================
   * Imports
   * ==========================================================
   */

  private renderImports(): React.ReactNode {
    const review = this.requireReview();

    return (
      <Paper
        variant="outlined"
        sx={{
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            p: 2,
          }}
        >
          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            justifyContent="space-between"
            alignItems={{
              xs: "stretch",
              sm: "center",
            }}
            spacing={1}
          >
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Worksheet Imports
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Sheets recognized and extracted into the onboarding staging
                layer.
              </Typography>
            </Box>

            <Chip
              size="small"
              variant="outlined"
              icon={<DatasetOutlinedIcon />}
              label={`${this.formatNumber(review.imports.length)} imports`}
            />
          </Stack>
        </Box>

        <Divider />

        {review.imports.length === 0 ? (
          this.renderEmptyImports()
        ) : (
          <Box
            sx={{
              display: "grid",

              gridTemplateColumns: {
                xs: "1fr",

                md: "repeat(2, minmax(0, 1fr))",

                xl: "repeat(3, minmax(0, 1fr))",
              },

              gap: 1.5,

              p: 2,
            }}
          >
            {review.imports.map((item, index) =>
              this.renderImportCard(item, index),
            )}
          </Box>
        )}
      </Paper>
    );
  }

  private renderImportCard(
    item: IBieOnboardingStagedImportSummary,
    index: number,
  ): React.ReactNode {
    const tone = this.getImportTone(item);

    const importIdAvailable =
      item.rid !== null &&
      item.rid !== undefined &&
      String(item.rid).trim() !== "";

    return (
      <Paper
        key={String(item.rid || item.sheetName || index)}
        variant="outlined"
        sx={{
          height: "100%",

          p: 1.75,

          borderColor: this.getToneBorderColor(tone),

          backgroundColor: this.getToneBackground(tone),
        }}
      >
        <Stack
          spacing={1.5}
          sx={{
            height: "100%",
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={1.5}
          >
            <Box
              sx={{
                minWidth: 0,
              }}
            >
              <Typography
                variant="subtitle1"
                fontWeight={700}
                noWrap
                title={item.sheetName}
              >
                {item.sheetName}
              </Typography>

              <Typography variant="caption" color="text.secondary">
                {item.className || getBieImportClassLabel(item.classCode)}
              </Typography>
            </Box>

            <Chip
              size="small"
              variant="outlined"
              color={this.getChipColor(tone)}
              icon={this.getOverallIcon(tone)}
              label={String(item.statusCode || "UNKNOWN").toUpperCase()}
            />
          </Stack>

          <Divider />

          <Box
            sx={{
              display: "grid",

              gridTemplateColumns: "repeat(4, minmax(0, 1fr))",

              gap: 1,
            }}
          >
            {this.renderImportCount("Rows", item.totalRows)}

            {this.renderImportCount("Staged", item.stagedRows)}

            {this.renderImportCount("Warnings", item.warningRows)}

            {this.renderImportCount("Rejected", item.rejectedRows)}
          </Box>

          {item.message && (
            <Alert
              severity={
                tone === "error"
                  ? "error"
                  : tone === "warning"
                    ? "warning"
                    : "info"
              }
              variant="outlined"
              sx={{
                py: 0,

                "& .MuiAlert-message": {
                  width: "100%",
                },
              }}
            >
              <Typography variant="caption">{item.message}</Typography>
            </Alert>
          )}

          {this.props.onOpenImportRecords && importIdAvailable && (
            <Box
              sx={{
                mt: "auto",
                pt: 0.5,
                textAlign: "right",
              }}
            >
              <Button
                size="small"
                variant="text"
                disabled={this.isDisabled()}
                endIcon={<OpenInNewOutlinedIcon />}
                onClick={() => this.handleOpenImport(item)}
              >
                View Staged Records
              </Button>
            </Box>
          )}
        </Stack>
      </Paper>
    );
  }

  private renderImportCount(label: string, value: number): React.ReactNode {
    return (
      <Box>
        <Typography variant="caption" color="text.secondary" display="block">
          {label}
        </Typography>

        <Typography variant="body2" fontWeight={700}>
          {this.formatNumber(value)}
        </Typography>
      </Box>
    );
  }

  private renderEmptyImports(): React.ReactNode {
    return (
      <Box
        sx={{
          py: 7,
          px: 3,

          textAlign: "center",
        }}
      >
        <DatasetOutlinedIcon
          color="disabled"
          sx={{
            fontSize: 52,
          }}
        />

        <Typography
          variant="h6"
          fontWeight={700}
          sx={{
            mt: 1,
          }}
        >
          No staged worksheet imports
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mt: 0.75,
          }}
        >
          The uploaded workbook did not produce any active staging Imports.
        </Typography>
      </Box>
    );
  }

  /* ==========================================================
   * Loading / unavailable
   * ==========================================================
   */

  private renderLoading(): React.ReactNode {
    return (
      <Paper
        variant="outlined"
        sx={{
          py: 8,
          px: 3,

          textAlign: "center",
        }}
      >
        <CircularProgress />

        <Typography
          variant="body1"
          fontWeight={600}
          sx={{
            mt: 2,
          }}
        >
          Loading staging and resolution review…
        </Typography>
      </Paper>
    );
  }

  private renderUnavailable(): React.ReactNode {
    return (
      <Paper
        variant="outlined"
        sx={{
          py: 8,
          px: 3,

          textAlign: "center",
        }}
      >
        <DatasetOutlinedIcon
          color="disabled"
          sx={{
            fontSize: 54,
          }}
        />

        <Typography
          variant="h6"
          fontWeight={700}
          sx={{
            mt: 1,
          }}
        >
          Staging results unavailable
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mt: 0.75,
          }}
        >
          Run Stage and Resolve from the Workbench before reviewing worksheet
          staging outcomes.
        </Typography>
      </Paper>
    );
  }

  /* ==========================================================
   * Event handlers
   * ==========================================================
   */

  private readonly handleRefresh = (): void => {
    const review = this.props.review;

    if (!review || !this.props.onRefresh) {
      return;
    }

    this.props.onRefresh(review.loadSessionId);
  };

  private handleOpenImport(item: IBieOnboardingStagedImportSummary): void {
    if (
      !this.props.onOpenImportRecords ||
      item.rid === null ||
      item.rid === undefined
    ) {
      return;
    }

    this.props.onOpenImportRecords(item.rid, item);
  }

  /* ==========================================================
   * Calculations
   * ==========================================================
   */

  private getTotals(
    review: IBieOnboardingWorkbenchStagingData,
  ): IBieStagingTotals {
    return review.imports.reduce<IBieStagingTotals>(
      (totals, item) => {
        const statusCode = String(item.statusCode || "").toUpperCase();

        return {
          imports: totals.imports + 1,

          totalRows: totals.totalRows + this.toNumber(item.totalRows),

          stagedRows: totals.stagedRows + this.toNumber(item.stagedRows),

          warningRows: totals.warningRows + this.toNumber(item.warningRows),

          rejectedRows: totals.rejectedRows + this.toNumber(item.rejectedRows),

          stagedImports:
            totals.stagedImports + (statusCode === "STAGED" ? 1 : 0),

          warningImports:
            totals.warningImports + (statusCode === "WARNING" ? 1 : 0),

          failedImports:
            totals.failedImports + (statusCode === "FAILED" ? 1 : 0),

          emptyImports: totals.emptyImports + (statusCode === "EMPTY" ? 1 : 0),
        };
      },
      {
        imports: 0,

        totalRows: 0,
        stagedRows: 0,
        warningRows: 0,
        rejectedRows: 0,

        stagedImports: 0,
        warningImports: 0,
        failedImports: 0,
        emptyImports: 0,
      },
    );
  }

  private getOverallTone(
    review: IBieOnboardingWorkbenchStagingData,
    totals: IBieStagingTotals,
  ): BieStagingTone {
    if ((review.errors?.length || 0) > 0 || totals.failedImports > 0) {
      return "error";
    }

    if (
      (review.warnings?.length || 0) > 0 ||
      totals.warningImports > 0 ||
      totals.warningRows > 0 ||
      totals.rejectedRows > 0
    ) {
      return "warning";
    }

    if (totals.imports > 0 && totals.stagedRows > 0) {
      return "success";
    }

    return "info";
  }

  private getImportTone(
    item: IBieOnboardingStagedImportSummary,
  ): BieStagingTone {
    const statusCode = String(item.statusCode || "").toUpperCase();

    if (statusCode === "FAILED" || this.toNumber(item.rejectedRows) > 0) {
      return "error";
    }

    if (statusCode === "WARNING" || this.toNumber(item.warningRows) > 0) {
      return "warning";
    }

    if (statusCode === "STAGED" && this.toNumber(item.stagedRows) > 0) {
      return "success";
    }

    return "default";
  }

  /* ==========================================================
   * Presentation helpers
   * ==========================================================
   */

  private getOverallLabel(tone: BieStagingTone): string {
    switch (tone) {
      case "success":
        return "Staged Successfully";

      case "warning":
        return "Staged with Warnings";

      case "error":
        return "Staging Problems";

      case "info":
        return "Review Required";

      default:
        return "Unknown";
    }
  }

  private getOverallIcon(tone: BieStagingTone): React.ReactElement {
    switch (tone) {
      case "success":
        return <CheckCircleOutlineIcon />;

      case "warning":
        return <WarningAmberOutlinedIcon />;

      case "error":
        return <ErrorOutlineIcon />;

      case "info":
      case "default":
      default:
        return <FactCheckOutlinedIcon />;
    }
  }

  private getChipColor(tone: BieStagingTone): ChipProps["color"] {
    switch (tone) {
      case "success":
        return "success";

      case "warning":
        return "warning";

      case "error":
        return "error";

      case "info":
        return "info";

      case "default":
      default:
        return "default";
    }
  }

  private getToneTextColor(tone: BieStagingTone): string {
    switch (tone) {
      case "success":
        return "success.main";

      case "warning":
        return "warning.main";

      case "error":
        return "error.main";

      case "info":
        return "info.main";

      case "default":
      default:
        return "text.primary";
    }
  }

  private getToneBorderColor(tone: BieStagingTone): string {
    switch (tone) {
      case "success":
        return "success.main";

      case "warning":
        return "warning.main";

      case "error":
        return "error.main";

      case "info":
        return "info.main";

      case "default":
      default:
        return "divider";
    }
  }

  private getToneBackground(tone: BieStagingTone): string {
    switch (tone) {
      case "success":
        return "rgba(46, 125, 50, 0.06)";

      case "warning":
        return "rgba(237, 108, 2, 0.06)";

      case "error":
        return "rgba(211, 47, 47, 0.06)";

      case "info":
        return "rgba(2, 136, 209, 0.06)";

      case "default":
      default:
        return "rgba(0, 0, 0, 0.025)";
    }
  }

  /* ==========================================================
   * General helpers
   * ==========================================================
   */

  private requireReview(): IBieOnboardingWorkbenchStagingData {
    if (!this.props.review) {
      throw new Error("Onboarding staging review data is required.");
    }

    return this.props.review;
  }

  private isDisabled(): boolean {
    return Boolean(
      this.props.disabled || this.props.loading || this.props.refreshing,
    );
  }

  private toNumber(value: unknown): number {
    const numericValue = Number(value);

    return Number.isFinite(numericValue) ? numericValue : 0;
  }

  private formatNumber(value: unknown): string {
    return new Intl.NumberFormat().format(this.toNumber(value));
  }
}
